import React, { useMemo, useState } from "react";
import { Modal, Spin } from "antd";
import { IconAlert } from "../Layout/Icons";
import ImumTdPreview from "./ImumTdPreview";
import ImumTableTD from "./ImumTableTD";
import {
  antdMessageInfo,
  numberRound,
  prettyDateFormat,
  relDiff,
} from "../../helpers/Utils";
import moment from "moment";
import ImumOldPricePreview from "./ImumOldPricePreview";
import ImumNewPriceChange from "./ImumNewPriceChange";
import { ButtonDelete } from "../common/Buttons";
import { useDispatch, useSelector } from "react-redux";
import AntdConfirm from "../../helpers/AntdConfirm";
import {
  exportImumToAlso,
  imumCheckIfHaveDifference,
} from "../../redux/Actions/imum_action";
import { count_how_much_the_amount_has_changed } from "../../helpers/calculations";

export default function ImumModalPreliminaryPreview({
  preliminaryPreview,
  setPreliminaryPreview,
}) {
  const dispatch = useDispatch();
  const { data, current, loading } = useSelector((state) => state.custom);
  // states
  const [active, setActive] = useState(false);
  const countries = preliminaryPreview.record?.lang || [];
  const over_ids = preliminaryPreview.record?.over_ids || [];
  const filterString = preliminaryPreview.record?.filterString || "";

  // Other
  const title = true ? (
    <div className="font-semi-bold">
      <IconAlert />
      Price change reduced more than 15%
    </div>
  ) : (
    "title"
  );

  const default_columns = [
    {
      title: "",
    },
    {
      title: "Prime cost",
    },
    {
      title: "Old price",
      lang: "lv",
    },

    {
      title: "New price",
      lang: "lv",
    },

    {
      title: "Old price changes in %",
      lang: "lv",
    },

    {
      title: "",
    },
  ];

  return (
    <Modal
      title={title}
      visible={true}
      width={window.innerWidth - 100}
      onOk={async () => {
        const variables = {
          ids: data.map((d) => d.id),
          lang: countries,
        };
        if (variables.ids.length == 0) return antdMessageInfo("Not selected");

        const have_more_when_15 = await imumCheckIfHaveDifference(variables);
        if (have_more_when_15.success) {
          AntdConfirm({
            title: "Are you sure to change prices?",
            content: "Some product prices were reduced more than 15%",
            onOk: () => {
              dispatch(
                exportImumToAlso(
                  variables.lang,
                  "ids",
                  variables.ids,
                  filterString
                )
              ).then((res) => {
                if (res.success) {
                  setPreliminaryPreview({
                    preview: false,
                    record: {},
                  });
                }
              });
            },
          });
        } else {
          dispatch(
            exportImumToAlso(variables.lang, "ids", variables.ids, filterString)
          ).then((res) => {
            if (res.success) {
              setPreliminaryPreview({
                preview: false,
                record: {},
              });
            }
          });
        }
        console.log({ variables });
      }}
      onCancel={() => setPreliminaryPreview({ preview: false, record: {} })}
    >
      {loading || current !== "ImumModal" ? (
        <LoadingComp />
      ) : (
        <>
          <small>
            Total selected: <b>{data.length}</b>
          </small>
          <div className="table-responsive">
            <table className="table table-hover mt-3 ">
              <thead>
                <tr className="text-muted">
                  {default_columns.map((d, idx) => (
                    <th
                      className={
                        idx == 0
                          ? "bg-white align-middle text-black"
                          : `bg-white align-middle text-black ${
                              d.lang ? "px-0" : ""
                            }`
                      }
                      key={`${d.title}_${d.lang}`}
                      colSpan={d.lang ? countries.length : 1}
                    >
                      <div className="">
                        {d.lang && (
                          <div
                            className={`font-semi-bold text-sm mb-2 text-center`}
                          >
                            {d.title}
                          </div>
                        )}
                        <ImumTdPreview
                          item={d}
                          shorting={
                            ["Price change", "New margin"].includes(d.title)
                              ? true
                              : false
                          }
                          values={{
                            countries,
                          }}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data
                  .filter((imum_product) => over_ids.includes(imum_product.id))
                  .map((d) => {
                    return (
                      <PreliminaryPreviewColumn
                        key={d.id}
                        {...d}
                        countries={countries}
                        active={active}
                        setActive={setActive}
                      />
                    );
                  })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

const PreliminaryPreviewColumn = ({
  active,
  setActive,
  id,
  cost,
  product_code,
  product_name,
  updated_at,
  new_prices,
  old_prices,
  cheapest,
  today_supplier_prices,
  price_change_history,
  countries,
}) => {
  const dispatch = useDispatch();
  const values = useMemo(() => {
    // new
    const valuess = {
      lt: {},
      ee: {},
      lv: {},
      fi: {},
      changed_price: false,
    };
    //  new price
    new_prices.forEach((d) => {
      valuess[d.country.toLowerCase()] = {
        ...valuess[d.country.toLowerCase()],
        new_price: d.price,
        id: d.id || null,
      };
    });

    price_change_history.forEach((d) => {
      const isToday =
        moment().format("YYYY-MM-DD") ==
        moment(d.created_at).format("YYYY-MM-DD");

      if (!isToday) return;
      // console.log({ isToday });

      const updated_at = `${prettyDateFormat(d.created_at)} - ${
        d.full_name
      } - ${numberRound(d.price)}`;
      if (d.country.toLowerCase().includes("_ee")) {
        if (valuess.ee.updated_at) return;
        valuess.ee = {
          ...valuess.ee,
          updated_at,
        };
      } else if (d.country.toLowerCase().includes("_lv")) {
        if (valuess.lv.updated_at) return;
        valuess.lv = {
          ...valuess.lv,
          updated_at,
        };
      } else {
        if (valuess.lt.updated_at) return;
        valuess.lt = {
          ...valuess.lt,
          updated_at,
        };
      }
    });

    cheapest.forEach((d) => {
      valuess[d.country.toLowerCase()] = {
        ...valuess[d.country.toLowerCase()],
        cheapest_price: d.price,
        cheapest:
          today_supplier_prices.find(
            (s) =>
              d.price == s.price &&
              s.category.toLowerCase() == d.country.toLowerCase()
          )?.shop || "-",
        // new_margin:
        //   d.price && cost
        //   ? `${numberRound(getPercentageChange(d.price, cost))} %`
        //   : "-",
      };
    });

    old_prices.forEach((d) => {
      let new_obj = {
        ...valuess[d.country.toLowerCase()],
        old_price: d.price,
        price_updated_at: d.price_updated_at
          ? prettyDateFormat(d.price_updated_at)
          : null,
      };
      if (+new_obj.new_price < +d.price) {
        const suspectful_price_diff = relDiff(+new_obj.new_price, +d.price);
        if (suspectful_price_diff >= 10)
          new_obj.suspectful_price = +numberRound(suspectful_price_diff);
      }

      valuess[d.country.toLowerCase()] = new_obj;
    });

    // price_changes.forEach((d) => {
    //   valuess[d.country.toLowerCase()] = {
    //     ...valuess[d.country.toLowerCase()],
    //     price_changes: d.price,
    //   };
    // });

    // OOS setter
    // const ossFuncOfArray = (category) => {
    //   let arr = today_supplier_prices.filter((d) => d.category == category);
    //   if (arr.length == 0) return true;
    //   return arr.every((d) => !d.active);
    // };
    // valuess.oos = [];
    // if (ossFuncOfArray("LT")) valuess.oos.push("LT");
    // if (ossFuncOfArray("LV")) valuess.oos.push("LV");
    // if (ossFuncOfArray("EE")) valuess.oos.push("EE");
    // if (ossFuncOfArray("FI")) valuess.oos.push("FI");

    valuess.related_ids = [valuess.lt.id, valuess.ee.id, valuess.lv.id].map(
      (d) => d
    );

    return valuess;
  }, [updated_at]);

  console.log({ values });

  const renderChangedPrices = (price) => {
    if (price > 15 || price < -15) {
      return <span className="text-danger">{price} %</span>;
    }
    return price + " %";
  };

  return (
    <tr>
      <td className="align-middle">
        <div
          className="text-sm"
          style={{
            lineHeight: "14px",
          }}
        >
          {product_name}
        </div>
        <div
          className="text-xs"
          style={{
            color: "#5F5F5F",
            lineHeight: "10px",
          }}
        >
          {product_code}
        </div>
      </td>
      <td className="align-middle">
        <div
          className="text-sm"
          style={{
            lineHeight: "14px",
          }}
        >
          {cost}
        </div>
      </td>
      {/* old price */}
      <ImumTableTD country="LT" countriesAreDisplayed={countries}>
        <ImumOldPricePreview value={values.lt} />
      </ImumTableTD>
      <ImumTableTD country="LV" countriesAreDisplayed={countries}>
        <ImumOldPricePreview value={values.lv} />
      </ImumTableTD>
      <ImumTableTD country="EE" countriesAreDisplayed={countries}>
        <ImumOldPricePreview value={values.ee} />
      </ImumTableTD>
      <ImumTableTD country="FI" countriesAreDisplayed={countries}>
        <ImumOldPricePreview value={values.fi} />
      </ImumTableTD>
      {/* new price */}
      <ImumNewPriceChange
        active={active}
        setActive={setActive}
        country="LT"
        value={values.lt.new_price}
        id={values.lt.id}
        parent_id={id}
        countriesAreDisplayed={countries}
        old_price={values.lt.old_price}
        cheapest_price={values.lt.cheapest_price}
        suspectful_price={values.lt.suspectful_price}
        related_ids={values.related_ids}
        variables={values.lt}
      />
      <ImumNewPriceChange
        active={active}
        setActive={setActive}
        country="LV"
        value={values.lv.new_price}
        id={values.lv.id}
        parent_id={id}
        countriesAreDisplayed={countries}
        old_price={values.lv.old_price}
        cheapest_price={values.lv.cheapest_price}
        suspectful_price={values.lv.suspectful_price}
        related_ids={values.related_ids}
        variables={values.lv}
      />
      <ImumNewPriceChange
        active={active}
        setActive={setActive}
        country="EE"
        value={values.ee.new_price}
        id={values.ee.id}
        parent_id={id}
        countriesAreDisplayed={countries}
        old_price={values.ee.old_price}
        cheapest_price={values.ee.cheapest_price}
        suspectful_price={values.ee.suspectful_price}
        related_ids={values.related_ids}
        variables={values.ee}
      />
      <ImumNewPriceChange
        active={active}
        setActive={setActive}
        country="FI"
        value={values.fi.new_price}
        id={values.fi.id}
        parent_id={id}
        countriesAreDisplayed={countries}
        old_price={values.fi.old_price}
        cheapest_price={values.fi.cheapest_price}
        suspectful_price={values.fi.suspectful_price}
        related_ids={values.related_ids}
        variables={values.fi}
      />
      <ImumTableTD country="LT" countriesAreDisplayed={countries}>
        {renderChangedPrices(
          numberRound(
            count_how_much_the_amount_has_changed(
              values.lt.new_price,
              values.lt.old_price
            )
          )
        )}
      </ImumTableTD>
      <ImumTableTD country="LV" countriesAreDisplayed={countries}>
        {renderChangedPrices(
          numberRound(
            count_how_much_the_amount_has_changed(
              values.lv.new_price,
              values.lv.old_price
            )
          )
        )}
      </ImumTableTD>
      <ImumTableTD country="EE" countriesAreDisplayed={countries}>
        {renderChangedPrices(
          numberRound(
            count_how_much_the_amount_has_changed(
              values.ee.new_price,
              values.ee.old_price
            )
          )
        )}
      </ImumTableTD>
      <ImumTableTD country="FI" countriesAreDisplayed={countries}>
        {renderChangedPrices(
          numberRound(
            count_how_much_the_amount_has_changed(
              values.fi.new_price,
              values.fi.old_price
            )
          )
        )}
      </ImumTableTD>
      <td className="align-middle" style={{ width: "50px" }}>
        <ButtonDelete
          onDelete={() => {
            AntdConfirm({
              onOk: () => {
                dispatch({
                  type: "REMOVE_CUSTOM",
                  payload: id,
                });
              },
            });
          }}
        />
      </td>
    </tr>
  );
};

const LoadingComp = () => (
  <div className="d-flex align-items-center justify-content-center my-2">
    <Spin />
  </div>
);
